import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { HTMLTable, NonIdealState } from '@blueprintjs/core';

import LoadingScreen from '@f/components/shared/loading-screen/LoadingScreen';

import { CenteredContainer } from '../centered-container/CenteredContainer';

import Row from './components/Row';
import cls from './Table.module.css';

function getSurroundingComponent(displayName) {
  return class extends PureComponent {
    static propTypes = {
      children: PropTypes.any,
      className: PropTypes.string,
    };

    static displayName = displayName;

    render() {
      return <div {...this.props}>{this.props.children}</div>;
    }
  };
}

const tableBeforeName = 'TableBefore';
export const TableBefore = getSurroundingComponent(tableBeforeName);

const tableAfterName = 'TableAfter';
export const TableAfter = getSurroundingComponent(tableAfterName);

const defaultEmptyStateProps = {
  icon: 'circle',
  title: 'No items',
  description: 'The list is empty.',
  action: null,
};

export default class extends PureComponent {
  static propTypes = {
    children: PropTypes.any,
    className: PropTypes.string,
    schema: PropTypes.array,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      })
    ),
    interactive: PropTypes.bool,
    selectable: PropTypes.bool,
    isRowSelected: PropTypes.func,
    loading: PropTypes.bool,
    loadingCaption: PropTypes.string,
    tableProps: PropTypes.object,
    headingRowProps: PropTypes.object,
    rowProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    emptyStateProps: PropTypes.object,
    onRowClick: PropTypes.func,
    onRowDoubleClick: PropTypes.func,
    onRowSelectionChange: PropTypes.func,
    onAllRowsSelectionChange: PropTypes.func,
  };

  static defaultProps = {
    className: '',
    schema: [],
    items: [],
    interactive: true,
    selectable: false,
    isRowSelected: () => false,
    loading: false,
    loadingCaption: 'Loading items...',
    tableProps: {},
    headingRowProps: {},
    rowProps: {},
    emptyStateProps: {},
    onRowClick: () => {},
    onRowDoubleClick: () => {},
    onRowSelectionChange: () => {},
    onAllRowsSelectionChange: () => {},
    onHover: () => {},
    onMouseLeave: () => {},
    hoveredRow: null,
  };

  static Before = TableBefore;

  static After = TableAfter;

  buildRowProps = (item, idx) => {
    const { rowProps } = this.props;

    if (typeof rowProps === 'function') {
      return rowProps(item, idx);
    }

    return rowProps;
  };

  buildEmptyStateProps = () => {
    const { emptyStateProps } = this.props;

    return {
      ...defaultEmptyStateProps,
      ...emptyStateProps,
    };
  };

  render() {
    const {
      children,
      className,
      schema,
      items,
      interactive,
      selectable,
      isRowSelected,
      loading,
      loadingCaption,
      tableProps,
      headingRowProps,
      rowProps,
      emptyStateProps,
      onRowClick,
      onRowDoubleClick,
      onRowSelectionChange,
      onAllRowsSelectionChange,
      onHover,
      onMouseLeave,
      hoveredRow,
      ...restProps
    } = this.props;
    const {
      icon: emptyIcon,
      title: emptyTitle,
      description: emptyDescription,
      action: emptyAction,
    } = this.buildEmptyStateProps();

    let tableBefore;
    let tableAfter;

    React.Children.forEach(children, child => {
      const displayName = child?.type?.displayName;

      if (displayName === tableBeforeName) {
        tableBefore = child;
      } else if (displayName === tableAfterName) {
        tableAfter = child;
      }
    });

    const tableEmpty = items.length === 0;

    const allRowsProps = items.map((item, idx) => this.buildRowProps(item, idx));
    const allRowsSelection = items.map((item, idx) => isRowSelected(item, idx));

    return (
      <div className={className} {...restProps}>
        {tableBefore}

        {loading && tableEmpty && <LoadingScreen caption={loadingCaption} />}

        {!loading && tableEmpty && (
          <CenteredContainer>
            <NonIdealState icon={emptyIcon} title={emptyTitle} description={emptyDescription} action={emptyAction} />
          </CenteredContainer>
        )}

        {!tableEmpty && (
          <div className={cls.overflowContainer}>
            <HTMLTable className={cls.table} striped interactive={interactive} {...tableProps}>
              <thead>
                <Row.Heading
                  schema={schema}
                  items={items}
                  selectable={selectable}
                  allRowsSelection={allRowsSelection}
                  trProps={headingRowProps}
                  onAllRowsSelectionChange={onAllRowsSelectionChange}
                />
              </thead>

              <tbody>
                {items.length > 0 &&
                  items.map((item, idx) => (
                    <Row
                      schema={schema}
                      item={item}
                      idx={idx}
                      selectable={selectable}
                      selected={allRowsSelection[idx]}
                      rowProps={allRowsProps[idx]}
                      onClick={onRowClick}
                      onDoubleClick={onRowDoubleClick}
                      onRowSelectionChange={onRowSelectionChange}
                      key={item.id}
                      onHover={onHover}
                      onMouseLeave={onMouseLeave}
                      hoveredRow={hoveredRow}
                    />
                  ))}
              </tbody>
            </HTMLTable>
          </div>
        )}

        {tableAfter}
      </div>
    );
  }
}
